import dayjs from 'dayjs';
import HistoricalService from '@/domain/Historical/HistoricalService';
import { getResumeFromDevice } from '@/services/measurement';
import { paramsCardEventsService } from '@/events/paramsCard.subject.js';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { AppDispatch, RootState } from '../store';

const historicalService = new HistoricalService();

// Definimos el tipo de estado para `readingsResumes`
interface Reading {
  [key: string]: any;
}

interface Resume {
  [key: string]: any;
}

interface ReadingsResumesState {
  readings: Reading[];
  readingsZoom: Reading[];
  resumes: Resume[];
}

// Estado inicial de `readingsResumes`
const initialState: ReadingsResumesState = {
  readings: [],
  readingsZoom: [],
  resumes: [],
};

const readingsResumesSlice = createSlice({
  name: 'readingsResumes',
  initialState,
  reducers: {
    addReadingToList(state, action: PayloadAction<Reading>) {
      state.readings.push(action.payload);
    },
    addZoomReadingToList(state, action: PayloadAction<Reading>) {
      state.readingsZoom.push(action.payload);
    },
    addResumeToList(state, action: PayloadAction<Resume>) {
      state.resumes.push(action.payload);
    },
    addResumeAndReadingToList(
      state,
      action: PayloadAction<{ resume: Resume; reading: Reading }>,
    ) {
      state.resumes.push(action.payload.resume);
      state.readings.push(action.payload.reading);
    },
    setAllReadingsList(state, action: PayloadAction<Reading[]>) {
      state.readings = action.payload;
    },
    setAllZoomReadingsList(state, action: PayloadAction<Reading[]>) {
      state.readingsZoom = action.payload;
    },
    setAllResumesList(state, action: PayloadAction<Resume[]>) {
      state.resumes = action.payload;
    },
    setAllList(
      state,
      action: PayloadAction<{
        readings: Reading[];
        resumes: Resume[];
        readingsZoom?: Reading[];
      }>,
    ) {
      state.readings = action.payload.readings;
      state.resumes = action.payload.resumes;
      if (action.payload.readingsZoom) {
        state.readingsZoom = action.payload.readingsZoom;
      }
    },
  },
});

export const {
  addReadingToList,
  addZoomReadingToList,
  addResumeToList,
  addResumeAndReadingToList,
  setAllReadingsList,
  setAllZoomReadingsList,
  setAllResumesList,
  setAllList,
} = readingsResumesSlice.actions;
export default readingsResumesSlice.reducer;

// Thunks para realizar acciones relacionadas con readings y resumes
export const addReadingToListThunk =
  (
    paremeterId: string,
    serialNumberSource: string,
    dateFrom: string,
    dataTo: string,
    originalSourceId: number | null = null,
    isOtherSource: boolean = false,
  ) =>
    async (dispatch: AppDispatch) => {
      paramsCardEventsService.setSubject({
        type: 'showReadingLoader',
        data: true,
      });
      const rangeDates = compareDates(dataTo, dateFrom);
      const response = await historicalService.getReadingsFromDeviceByParams(
        paremeterId,
        serialNumberSource,
        dayjs(dateFrom).format('YYYY-MM-DD HH:mm:ss'),
        dayjs(dataTo).format('YYYY-MM-DD HH:mm:ss'),
        parseInt(rangeDates[0]),
        rangeDates[1],
      );
      dispatch(addReadingToList({ ...response, originalSourceId, isOtherSource }));
      paramsCardEventsService.setSubject({
        type: 'showReadingLoader',
        data: false,
      });
      if (rangeDates[0] === '5') {
        dispatch(addZoomReadingToList(response));
      }
    };

export const addResumeToListThunk =
  (
    infrastructureId: string,
    paremeterId: string,
    serialNumberSource: string,
    dateFrom: string,
    dataTo: string,
  ) =>
    async (dispatch: AppDispatch) => {
      paramsCardEventsService.setSubject({
        type: 'showResumeLoader',
        data: true,
      });
      const response = await getResumeFromDevice(
        infrastructureId,
        paremeterId,
        serialNumberSource,
        dayjs(dateFrom).format('YYYY-MM-DD HH:mm:ss'),
        dayjs(dataTo).format('YYYY-MM-DD HH:mm:ss'),
      );
      dispatch(addResumeToList(response));
      paramsCardEventsService.setSubject({
        type: 'showResumeLoader',
        data: false,
      });
    };

export const addResumeAndReadingToListThunk =
  (
    infrastructureId: string,
    infraestructureType: string,
    paremeterId: string,
    serialNumberSource: string,
    dateFrom: string,
    dataTo: string,
  ) =>
    async (dispatch: AppDispatch) => {
      const rangeDates = compareDates(dataTo, dateFrom);
      paramsCardEventsService.setSubject({
        type: 'showReadingLoader',
        data: true,
      });
      paramsCardEventsService.setSubject({
        type: 'showResumeLoader',
        data: true,
      });
      const reading = await historicalService.getReadingsFromDeviceByParams(
        paremeterId,
        null,
        dayjs(dateFrom).format('YYYY-MM-DD HH:mm:ss'),
        dayjs(dataTo).format('YYYY-MM-DD HH:mm:ss'),
        rangeDates[0],
        rangeDates[1],
      );
      dispatch(addReadingToList({ ...reading, infraestructureType }));
      if (rangeDates[0] === '5') {
        dispatch(addZoomReadingToList({ ...reading, infraestructureType }));
      }
      paramsCardEventsService.setSubject({
        type: 'showReadingLoader',
        data: false,
      });

      const resume = await getResumeFromDevice(
        infrastructureId,
        paremeterId,
        null,
        dayjs(dateFrom).format('YYYY-MM-DD HH:mm:ss'),
        dayjs(dataTo).format('YYYY-MM-DD HH:mm:ss'),
      );
      dispatch(addResumeToList(resume));
      paramsCardEventsService.setSubject({
        type: 'showResumeLoader',
        data: false,
      });
    };

export const getAllListThunk =
  (selectedSensors: any[], dateFrom: string, dataTo: string) =>
    async (dispatch: AppDispatch) => {
      const rangeDates = compareDates(dataTo, dateFrom);

      paramsCardEventsService.setSubject({
        type: 'showReadingLoader',
        data: true,
      });
      paramsCardEventsService.setSubject({
        type: 'showResumeLoader',
        data: true,
      });

      const readingsPromises = selectedSensors.map((sensor) => {
        if (sensor.measurementTargetId) {
          return historicalService.getReadingsFromDeviceByParams(
            sensor.measurementTargetId,
            null,
            dayjs(dateFrom).format('YYYY-MM-DD HH:mm:ss'),
            dayjs(dataTo).format('YYYY-MM-DD HH:mm:ss'),
            rangeDates[0],
            rangeDates[1],
          );
        }
      });

      const resumesPromises = selectedSensors.map((sensor) => {
        if (sensor.measurementTargetId) {
          return getResumeFromDevice(
            sensor.infrastructureId,
            sensor.measurementTargetId,
            sensor.serialNumberMeasurementTool,
            dayjs(dateFrom).format('YYYY-MM-DD HH:mm:ss'),
            dayjs(dataTo).format('YYYY-MM-DD HH:mm:ss'),
          );
        }
      });

      let readings = await Promise.all(readingsPromises);
      readings = readings.map((reading) => ({ ...reading, infraestructureType: selectedSensors.find((sensor) => sensor.infrastructureId === reading.infrastructureId)?.infrastructureTypeId }));

      dispatch(setAllReadingsList(readings));
      if (rangeDates[0] === '5') {
        dispatch(setAllZoomReadingsList(readings));
      }
      paramsCardEventsService.setSubject({
        type: 'showReadingLoader',
        data: false,
      });

      const resumes = await Promise.all(resumesPromises);
      dispatch(setAllResumesList(resumes));
      paramsCardEventsService.setSubject({
        type: 'showResumeLoader',
        data: false,
      });
    };

export const getAllReadingsThunk =
  (selectedSensors: any[], dateFrom: string, dataTo: string) =>
    async (dispatch: AppDispatch) => {
      const rangeDates = compareDates(dataTo, dateFrom);

      paramsCardEventsService.setSubject({
        type: 'showReadingLoader',
        data: true,
      });

      const readingsPromises = selectedSensors.map((sensor) => {
        if (sensor.measurementTargetId) {
          return historicalService.getReadingsFromDeviceByParams(
            sensor.measurementTargetId,
            null,
            dayjs(dateFrom).format('YYYY-MM-DD HH:mm:ss'),
            dayjs(dataTo).format('YYYY-MM-DD HH:mm:ss'),
            rangeDates[0],
            rangeDates[1],
          );
        }
      });

      let readings = await Promise.all(readingsPromises);
      readings = readings.map((reading) => ({ ...reading, infraestructureType: selectedSensors.find((sensor) => sensor.infrastructureId === reading.infrastructureId)?.infrastructureTypeId }));
      dispatch(setAllReadingsList(readings));
      if (rangeDates[0] === '5') {
        dispatch(setAllZoomReadingsList(readings));
      }
      paramsCardEventsService.setSubject({
        type: 'showReadingLoader',
        data: false,
      });
    };

export const restartResumeAndReadingList = () => (dispatch: AppDispatch) => {
  dispatch(setAllList({ readings: [], resumes: [] }));
};

export const deleteResumeAndReadingToList =
  (paremeterId: string, serialNumberSource: string | null) =>
    (dispatch: AppDispatch, getState: () => any) => {
      const currentState: any = getState().readingsResumes;
      const resumes = currentState.resumes.filter(
        (resume) => resume && resume.parameterId !== paremeterId,
      );
      const readings = currentState.readings.filter(
        (reading) =>
          reading &&
          !(
            reading.originalSourceSerialNumber === serialNumberSource &&
            reading.originalParameterId === paremeterId
          ),
      );
      const readingsZoom = currentState.readingsZoom.filter(
        (reading) =>
          reading &&
          !(
            reading.originalSourceSerialNumber === serialNumberSource &&
            reading.originalParameterId === paremeterId
          ),
      );
      dispatch(setAllList({ resumes, readings, readingsZoom }));
    };

export const deleteResumeAndAllChildReadingsToList =
  (paremeterId: string) =>
    (dispatch: AppDispatch, getState: () => any) => {
      const currentState: any = getState().readingsResumes;
      const resumes = currentState.resumes.filter(
        (resume) => resume && resume.parameterId !== paremeterId,
      );
      console.log("🚀 ~ currentState.readings:", currentState.readings)

      const readings = currentState.readings.filter(
        (reading) => reading && !(reading.originalParameterId === paremeterId),
      );
      const readingsZoom = currentState.readingsZoom.filter(
        (reading) => reading && !(reading.originalParameterId === paremeterId),
      );
      dispatch(setAllList({ resumes, readings, readingsZoom }));
    };

export const deleteReadingFromList =
  (paramId: string, serialNumberSource: string) =>
    (dispatch: AppDispatch, getState: () => any) => {
      const currentState = getState().readingsResumes;
      const resumes = currentState.resumes;
      const readings = currentState.readings.filter(
        (reading) =>
          reading &&
          !(
            reading.originalSourceSerialNumber === serialNumberSource &&
            reading.originalParameterId === paramId
          ),
      );
      dispatch(setAllList({ resumes, readings }));
    };

function compareDates(date1: string, date2: string): [string, string] {
  const startDate = dayjs(date2);
  const endDate = dayjs(date1);
  const diffInMonths = endDate.diff(startDate, 'month', true);

  return ['5', 'minute'];
}
